
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { animate } from "framer-motion";
import styles from "./deck.module.css";

/* ─────────── Tuner config (read by DesignTuner + Reveal) ─────────── */
export type DirMode = "varied" | "uniform";

export interface DeckTunerState {
  scrollDuration: number; // section transition ms
  revealDuration: number; // per-element entrance animation ms
  revealDistance: number; // px
  textScale: number; // %
  bgGlow: number; // %
  varyDir: DirMode;
}

const DEFAULTS: DeckTunerState = {
  scrollDuration: 750,
  revealDuration: 700,
  revealDistance: 60,
  textScale: 100,
  bgGlow: 6,
  varyDir: "varied",
};

/* ─────────── Context API ─────────── */
interface DeckContextValue {
  registerSection: (idx: number, steps?: number) => (el: HTMLElement | null) => void;
  goToSection: (idx: number) => void;
  /** Move to a step inside the active section (see DeckSection `steps`). */
  goToStep: (step: number) => void;
  activeIdx: number;
  /** Step inside the active section; always 0 for a single-step section. */
  activeStep: number;
  total: number;
  tuner: DeckTunerState;
  setTuner: <K extends keyof DeckTunerState>(key: K, val: DeckTunerState[K]) => void;
  tunerPanelRef: React.MutableRefObject<HTMLDivElement | null>;
  showTuner: boolean;
}

const DeckContext = createContext<DeckContextValue | null>(null);

export function useDeck(): DeckContextValue {
  const ctx = useContext(DeckContext);
  if (!ctx) {
    throw new Error("useDeck must be used inside <SectionDeck>");
  }
  return ctx;
}

/* ─────────── Provider ─────────── */
interface SectionDeckProps {
  children: ReactNode;
  /** Optional CSS string for backdrop tint applied behind content. */
  background?: string;
  /** Initial tuner state overrides. */
  initialTuner?: Partial<DeckTunerState>;
  /** Show progress dots on the right edge (default true). */
  showProgressDots?: boolean;
}

export default function SectionDeck({
  children,
  background,
  initialTuner,
  showProgressDots = true,
}: SectionDeckProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sectionsRef = useRef<HTMLElement[]>([]);
  const stepsRef = useRef<number[]>([]);
  const activeIdxRef = useRef(0);
  const activeStepRef = useRef(0);
  const lockedRef = useRef(false);
  const tunerPanelRef = useRef<HTMLDivElement | null>(null);

  const [activeIdx, setActiveIdx] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [total, setTotal] = useState(0);
  const [showTuner, setShowTuner] = useState(false);

  const [tuner, setTunerState] = useState<DeckTunerState>(() => ({
    ...DEFAULTS,
    ...initialTuner,
  }));

  const setTuner = useCallback(
    <K extends keyof DeckTunerState>(key: K, val: DeckTunerState[K]) => {
      setTunerState((prev) => ({ ...prev, [key]: val }));
    },
    []
  );

  /* Detect ?tune=1 in URL on mount to enable tuner UI */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("tune") === "1") setShowTuner(true);
  }, []);

  const registerSection = useCallback(
    (idx: number, steps = 1) => (el: HTMLElement | null) => {
      if (el) {
        sectionsRef.current[idx] = el;
        stepsRef.current[idx] = steps;
        /* Total may grow as sections register on first render. */
        setTotal((t) => Math.max(t, idx + 1));
      }
    },
    []
  );

  /* Smooth-scroll the container to a specific section index. */
  const goToSection = useCallback(
    (target: number) => {
      const container = containerRef.current;
      const sections = sectionsRef.current;
      if (!container || sections.length === 0) return;

      const clamped = Math.max(0, Math.min(sections.length - 1, target));
      if (clamped === activeIdxRef.current || lockedRef.current) return;
      const previous = activeIdxRef.current;

      lockedRef.current = true;
      activeIdxRef.current = clamped;
      setActiveIdx(clamped);

      /* Sections can now scroll internally (see below). Land the incoming one
         at the edge you are arriving from — top when moving down, bottom when
         moving up — so reading order is continuous either way. Steps follow
         the same rule: first step going down, last step coming back up. */
      const incoming = sections[clamped];
      incoming.scrollTop = clamped > previous ? 0 : incoming.scrollHeight;
      const landStep = clamped > previous ? 0 : (stepsRef.current[clamped] ?? 1) - 1;
      activeStepRef.current = landStep;
      setActiveStep(landStep);

      const targetTop = incoming.offsetTop;
      const startTop = container.scrollTop;

      animate(startTop, targetTop, {
        duration: tuner.scrollDuration / 1000,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: (v) => {
          container.scrollTop = v;
        },
        onComplete: () => {
          window.setTimeout(() => {
            lockedRef.current = false;
          }, 60);
        },
      });
    },
    [tuner.scrollDuration]
  );

  /* Move between steps of the active section. The deck doesn't scroll — the
     section animates its own content off `activeStep` — but the gesture lock
     lasts as long as a section glide so steps keep the same pace. */
  const goToStep = useCallback(
    (target: number) => {
      const steps = stepsRef.current[activeIdxRef.current] ?? 1;
      const clamped = Math.max(0, Math.min(steps - 1, target));
      if (clamped === activeStepRef.current || lockedRef.current) return;

      lockedRef.current = true;
      activeStepRef.current = clamped;
      setActiveStep(clamped);
      window.setTimeout(() => {
        lockedRef.current = false;
      }, tuner.scrollDuration + 60);
    },
    [tuner.scrollDuration]
  );

  /* One gesture: the next step of a multi-step section, else the next section. */
  const advance = useCallback(
    (dir: 1 | -1) => {
      const steps = stepsRef.current[activeIdxRef.current] ?? 1;
      const next = activeStepRef.current + dir;
      if (next >= 0 && next < steps) goToStep(next);
      else goToSection(activeIdxRef.current + dir);
    },
    [goToSection, goToStep]
  );

  /* Capture wheel + touch + keyboard, drive scroll programmatically. */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isInsideTuner = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false;
      return tunerPanelRef.current?.contains(target) ?? false;
    };

    /* True while the active section still has content to scroll in `dir`.
       On a phone (or any short window) a section is taller than the viewport,
       and the deck must yield the gesture to it until it reaches the edge —
       otherwise everything below the fold can never be reached. */
    const canScrollWithin = (dir: 1 | -1): boolean => {
      const sec = sectionsRef.current[activeIdxRef.current];
      if (!sec) return false;
      const slack = sec.scrollHeight - sec.clientHeight;
      if (slack <= 1) return false;
      return dir > 0 ? sec.scrollTop < slack - 1 : sec.scrollTop > 1;
    };

    const onWheel = (e: WheelEvent) => {
      if (isInsideTuner(e.target)) return;
      if (Math.abs(e.deltaY) < 4) return;
      if (canScrollWithin(e.deltaY > 0 ? 1 : -1)) return; // let the section scroll
      e.preventDefault();
      if (lockedRef.current) return;
      advance(e.deltaY > 0 ? 1 : -1);
    };

    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (isInsideTuner(e.target)) return;
      const dy = touchStartY - (e.touches[0]?.clientY ?? 0);
      if (Math.abs(dy) < 24) return;
      if (canScrollWithin(dy > 0 ? 1 : -1)) return; // let the section scroll
      e.preventDefault();
      if (lockedRef.current) return;
      advance(dy > 0 ? 1 : -1);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.target instanceof HTMLTextAreaElement) return;
      if (lockedRef.current) return;
      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
        if (canScrollWithin(1)) return;
        e.preventDefault();
        advance(1);
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        if (canScrollWithin(-1)) return;
        e.preventDefault();
        advance(-1);
      } else if (e.key === "Home") {
        e.preventDefault();
        goToSection(0);
      } else if (e.key === "End") {
        e.preventDefault();
        goToSection(sectionsRef.current.length - 1);
      }
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKey);

    return () => {
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKey);
    };
  }, [advance, goToSection]);

  /* Lock body scroll while deck is mounted.
     The deck is position:fixed inside a possibly-transformed ancestor
     (PageTransition wraps every page in a motion.div with translate(0,0)
     after animation, which establishes a containing block). Without this
     lock, body scroll can shift the wrapper up and the global Footer
     pokes through below the deck, making centered content appear to
     "drop" or "shift" as the viewport changes effective height.
     <html> is locked too: globals.css gives it overflow-x: clip, which stops
     body's overflow from reaching the viewport, so the page scrolls on <html>.
     With only <body> locked, a wheel outside the deck (over the nav) scrolled
     the page by the footer's height and the footer sat under every section. */
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    const prevHtml = html.style.overflow;
    const prevBody = document.body.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, []);

  const ctxValue = useMemo<DeckContextValue>(
    () => ({
      registerSection,
      goToSection,
      goToStep,
      activeIdx,
      activeStep,
      total,
      tuner,
      setTuner,
      tunerPanelRef,
      showTuner,
    }),
    [registerSection, goToSection, goToStep, activeIdx, activeStep, total, tuner, setTuner, showTuner]
  );

  const containerStyle: React.CSSProperties = {
    "--text-scale": `${tuner.textScale / 100}rem`,
    "--bg-glow": `${tuner.bgGlow / 100}`,
    background:
      background ??
      `radial-gradient(ellipse at 50% 0%, hsla(262, 85%, 62%, var(--bg-glow, 0.06)) 0%, transparent 60%), var(--color-bg-primary, #0a0a0c)`,
  } as React.CSSProperties;

  return (
    <DeckContext.Provider value={ctxValue}>
      <div ref={containerRef} className={styles.deck} style={containerStyle}>
        {children}
      </div>
      {showProgressDots && total > 1 && (
        <div className={styles.progressRail}>
          {Array.from({ length: total }).map((_, i) => (
            <button
              key={i}
              className={`${styles.progressDot} ${i === activeIdx ? styles.progressDotActive : ""}`}
              onClick={() => goToSection(i)}
              aria-label={`Go to section ${i + 1}`}
            />
          ))}
        </div>
      )}
    </DeckContext.Provider>
  );
}
